'use client';

import { FC, useEffect, useRef, useState } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { Collapse, Typography } from '@mui/material';
import { useTranslations } from 'next-intl';
import { COLORS } from '@/constants';
import { WlasCheckIcon, WlasCopySmallIcon } from '@/components';
import { runtimeConfig } from '@/lib/runtimeConfig';
import {
  Card,
  CardCollapseBody,
  CardSubtitle,
  CardTitle,
  CardTitleHeader,
  CollapseToggle,
  MemberCodeBadge,
  MemberCodeLabel,
} from '@/features/pages/profile/wlas-widget/components/WlasTabsPanel/tabs/configuration/styled';
import {
  CalloutWrapper,
  DocsProse,
  DocsStepBody,
  DocsStepItem,
  DocsStepList,
  DocsStepNumber,
  HowItWorksBody,
  HowItWorksFooter,
  SnippetActions,
  SnippetCard,
  SnippetCopyBtn,
  SnippetHeader,
  SnippetPre,
  SnippetTitle,
  SrOnly,
} from './styled';
import { WlasChevronIcon } from '@/features/pages/profile/wlas-widget/components/WlasTabsPanel/tabs/configuration/components/WlasParamsCard/components/WlasSection/WlasChevronIcon';
import { WlasCallout } from '@/features/pages/profile/wlas-widget/components/WlasTabsPanel/tabs/configuration/components/WlasFaqCard/components/WlasCallout';

function useCopy(text: string) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const copy = () => {
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 2000);
    });
  };

  return { copied, copy };
}

interface CodeBlockProps {
  code: string;
  label: string;
  memberUniqueCode?: string;
}

const CodeBlock: FC<CodeBlockProps> = ({ code, label, memberUniqueCode }) => {
  const t = useTranslations('wlasWidget');
  const { copied, copy } = useCopy(code);

  return (
    <div>
      <SrOnly role="status" aria-live="polite" aria-atomic="true">
        {copied ? t('copied') : ''}
      </SrOnly>
      <SnippetCard>
        <SnippetHeader>
          <SnippetTitle>{label}</SnippetTitle>
          <SnippetActions>
            {memberUniqueCode && (
              <>
                <MemberCodeLabel>{t('snippet.memberCode')}</MemberCodeLabel>
                <MemberCodeBadge>{memberUniqueCode}</MemberCodeBadge>
              </>
            )}
            <SnippetCopyBtn type="button" onClick={copy}>
              {copied ? (
                <>
                  <WlasCheckIcon />
                  {t('copied')}
                </>
              ) : (
                <>
                  <WlasCopySmallIcon />
                  {t('snippet.copy')}
                </>
              )}
            </SnippetCopyBtn>
          </SnippetActions>
        </SnippetHeader>
        <SnippetPre>{code}</SnippetPre>
      </SnippetCard>
    </div>
  );
};

interface WlasQuickstartCardProps {
  memberUniqueCode: string;
}

export const WlasQuickstartCard: FC<WlasQuickstartCardProps> = ({ memberUniqueCode }) => {
  const t = useTranslations('wlasWidget');
  const [open, setOpen] = useLocalStorage('wlas-card-quickstart-open', true);
  const [howOpen, setHowOpen] = useLocalStorage('wlas-card-quickstart-how-open', false);

  const scriptSrc = `${runtimeConfig.WLAS_BASE_URL ?? ''}alleimmobilien-wlas-widget.js`;
  const step1Code = `<script src="${scriptSrc}" defer></script>`;
  const step2Code = `<alleimmobilien-wlas-widget\n  member-unique-code="${memberUniqueCode}"\n/>`;

  return (
    <Card>
      <CardTitleHeader onClick={() => setOpen((v) => !v)}>
        <div>
          <CardTitle>{t('quickstart.title')}</CardTitle>
          <CardSubtitle>{t('quickstart.subtitle')}</CardSubtitle>
        </div>
        <CollapseToggle
          type="button"
          aria-expanded={open}
          aria-controls="wlas-quickstart-body"
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
        >
          <WlasChevronIcon open={open} />
        </CollapseToggle>
      </CardTitleHeader>

      <Collapse in={open} aria-hidden={!open}>
        <CardCollapseBody id="wlas-quickstart-body">
          <DocsStepList>
            <DocsStepItem>
              <DocsStepNumber>1</DocsStepNumber>
              <DocsStepBody>
                <Typography fontSize={14} fontWeight={600} color={COLORS['primary/900']} mb={1}>
                  {t('quickstart.step1.label')}
                </Typography>
                <DocsProse>{t('quickstart.step1.hint')}</DocsProse>
                <CodeBlock code={step1Code} label={t('quickstart.step1.label')} />
              </DocsStepBody>
            </DocsStepItem>

            <DocsStepItem>
              <DocsStepNumber>2</DocsStepNumber>
              <DocsStepBody>
                <Typography fontSize={14} fontWeight={600} color={COLORS['primary/900']} mb={1}>
                  {t('quickstart.step2.label')}
                </Typography>
                <CodeBlock
                  code={step2Code}
                  label={t('quickstart.step2.label')}
                  memberUniqueCode={memberUniqueCode}
                />
                <CalloutWrapper>
                  <WlasCallout variant="info">{t('quickstart.step2.callout')}</WlasCallout>
                </CalloutWrapper>
              </DocsStepBody>
            </DocsStepItem>
          </DocsStepList>

          <HowItWorksFooter>
            <CollapseToggle
              type="button"
              aria-expanded={howOpen}
              aria-controls="wlas-howitworks-body"
              onClick={() => setHowOpen((v) => !v)}
              style={{ alignSelf: 'flex-start' }}
            >
              <Typography fontSize={14} fontWeight={600} color={COLORS['primary/700']}>
                {t('quickstart.howItWorks.title')}
              </Typography>
              <WlasChevronIcon open={howOpen} />
            </CollapseToggle>

            <Collapse in={howOpen} aria-hidden={!howOpen}>
              <HowItWorksBody id="wlas-howitworks-body">
                <DocsProse>{t('quickstart.howItWorks.body')}</DocsProse>
                <WlasCallout variant="tip">{t('quickstart.howItWorks.tipCallout')}</WlasCallout>
                <CalloutWrapper>
                  <WlasCallout variant="info">
                    {t('quickstart.howItWorks.shareLinkCallout')}
                  </WlasCallout>
                </CalloutWrapper>
              </HowItWorksBody>
            </Collapse>
          </HowItWorksFooter>
        </CardCollapseBody>
      </Collapse>
    </Card>
  );
};
